<script setup lang="ts">
	import { computed, ref } from 'vue';
	import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
	import { MzButton, MzNavigationLink, MzSidebar, type MzSidebarItem } from '@monizze/monizze-components';
	import { MzNotificationCenter, type NovuNotification } from '@monizze/monizze-components/notification-center';
	import { PhBell, PhChatCircle, PhGear, PhHouse, PhInfo, PhKey, PhListDashes, PhShield, PhUser } from '@phosphor-icons/vue';
	import { useI18n } from 'vue-i18n';
	import { useRoute, useRouter } from 'vue-router';
	import { useAuthStore } from '@/stores/auth.ts';
	import { useNotificationCenter } from '@/composables/useNotificationCenter';
	import { getNotificationUrl } from '@/types/Notification';
	import UserAccountMenu from '@/components/UserAccountMenu.vue';

	const { t } = useI18n();
	const route = useRoute();
	const router = useRouter();
	const authStore = useAuthStore();

	// Owned by the layout: below `lg` this is the drawer its mobile header opens.
	const open = defineModel<boolean>('open', { default: false });

	const isDesktop = useBreakpoints(breakpointsTailwind).greaterOrEqual('lg');

	const { config: novuConfig, subscribers, organizationId: novuOrganizationId } = useNotificationCenter();

	const items = computed<MzSidebarItem[]>(() => [
		{
			label: t('user.sidebar.dashboard'),
			to: '/user/dashboard',
			leadingIcon: PhHouse,
			// /partner is a sibling route, so RouterLink won't mark Home active there on its own.
			active: route.name === 'partner',
		},
		{
			label: t('user.sidebar.profile'),
			to: '/profile',
			leadingIcon: PhUser,
		},
		{
			label: t('user.sidebar.security'),
			to: '/security',
			leadingIcon: PhShield,
		},
		{
			label: t('user.sidebar.notifications'),
			to: '/notification-preferences',
			leadingIcon: PhChatCircle,
		},
		{
			label: t('user.sidebar.access'),
			to: '/user/access',
			leadingIcon: PhKey,
		},
	]);

	const openFaq = () => window.open(t('user.sidebar.faq_link'), '_blank', 'noopener');

	const footerItems = computed<MzSidebarItem[]>(() => [
		{
			label: t('user.sidebar.info'),
			leadingIcon: PhInfo,
			onSelect: openFaq,
		},
	]);

	const notificationUrl = (notification: NovuNotification): string | null => getNotificationUrl(notification.tags ?? []);

	const goToNotificationRoute = (path: string, closePanel: () => void) => {
		closePanel();
		router.push(path);
	};

	// Inside the mobile drawer the panel would open under the sidebar, so the bell goes straight to the list page.
	const notificationPanelOpen = ref(false);
	const setNotificationPanelOpen = (value: boolean) => {
		if (value && !isDesktop.value) {
			open.value = false;
			router.push('/notifications');
			return;
		}
		notificationPanelOpen.value = value;
	};
</script>

<template>
	<mz-sidebar
		v-model:open="open"
		:brand="authStore.isLux ? 'lux' : 'monizze'"
		home-to="/user/dashboard"
		:items="items"
		:footer-items="footerItems"
	>
		<template #footer>
			<mz-notification-center
				v-if="novuConfig"
				:open="notificationPanelOpen"
				:sound="false"
				:application-identifier="novuConfig.applicationIdentifier"
				:subscribers="subscribers"
				:api-url="novuConfig.apiUrl"
				:socket-url="novuConfig.socketUrl"
				:organization-id="novuOrganizationId"
				align="start"
				:notification-url="notificationUrl"
				@update:open="setNotificationPanelOpen"
			>
				<template #headerActions="{ close }">
					<mz-button
						variant="minimal"
						size="small"
						:icon="PhGear"
						:aria-label="t('user.sidebar.notification_preferences')"
						@click="goToNotificationRoute('/notification-preferences', close)"
					/>
					<mz-button
						variant="minimal"
						size="small"
						:icon="PhListDashes"
						:aria-label="t('user.sidebar.notification_list')"
						@click="goToNotificationRoute('/notifications', close)"
					/>
				</template>
				<template #trigger="{ unreadCount, open: panelOpen }">
					<mz-navigation-link
						:label="t('user.sidebar.inbox')"
						:leading-icon="PhBell"
						:notified="unreadCount"
						:active="panelOpen || route.path.startsWith('/notifications')"
						class="w-full"
					/>
				</template>
			</mz-notification-center>
		</template>

		<template #account="{ open: expanded }">
			<user-account-menu :side="expanded ? 'top' : 'right'" :icon-only="!expanded" />
		</template>
	</mz-sidebar>
</template>
